/**
 * Student Activity Manager - CMS Admin Page
 * Comprehensive view of all student portal activities
 * AI Finder searches, calculator usage, engagement metrics
 */
import { useState, useEffect } from 'react';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Activity,
  Search,
  Users,
  TrendingUp,
  RefreshCw,
  Eye,
  Calendar,
  Sparkles,
  Calculator,
  FileText,
  Upload,
  Clock,
  CheckCircle,
  XCircle,
  ExternalLink,
  ChevronRight,
  Filter,
  Download
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin || '';

// Activity category colors
const CATEGORY_COLORS = {
  application: 'bg-blue-100 text-blue-800',
  document: 'bg-emerald-100 text-emerald-800',
  milestone: 'bg-violet-100 text-violet-800',
  ai_finder: 'bg-purple-100 text-purple-800',
  calculator: 'bg-amber-100 text-amber-800',
  engagement: 'bg-slate-100 text-slate-800',
  content: 'bg-indigo-100 text-indigo-800',
  communication: 'bg-teal-100 text-teal-800',
  profile: 'bg-orange-100 text-orange-800',
  auth: 'bg-gray-100 text-gray-800',
};

export default function StudentActivityManager() {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentActivities, setStudentActivities] = useState(null);
  const [aiFinderActivities, setAiFinderActivities] = useState([]);
  const [syncQueue, setSyncQueue] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    loadDashboard();
    loadStudents();
    loadAiFinderActivities();
    loadSyncQueue();
  }, []);

  const loadDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/activity/admin/dashboard`);
      const data = await res.json();
      setDashboard(data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
      toast.error('Failed to load activity dashboard');
    }
  };

  const loadStudents = async () => {
    try {
      const res = await fetch(`${API_URL}/api/activity/admin/students?limit=100`);
      const data = await res.json();
      setStudents(data.students || []);
    } catch (error) {
      console.error('Error loading students:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAiFinderActivities = async () => {
    try {
      const res = await fetch(`${API_URL}/api/activity/admin/ai-finder?limit=50`);
      const data = await res.json();
      setAiFinderActivities(data.activities || []);
    } catch (error) {
      console.error('Error loading AI Finder activities:', error);
    }
  };

  const loadSyncQueue = async () => {
    try {
      const res = await fetch(`${API_URL}/api/activity/admin/zoho-sync-queue`);
      const data = await res.json();
      setSyncQueue(data.pending_sync || []);
    } catch (error) {
      console.error('Error loading sync queue:', error);
    }
  };

  const loadStudentActivities = async (userId) => {
    try {
      const res = await fetch(`${API_URL}/api/activity/admin/student/${userId}`);
      const data = await res.json();
      setStudentActivities(data);
    } catch (error) {
      console.error('Error loading student activities:', error);
      toast.error('Failed to load student activities');
    }
  };

  const triggerZohoSync = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/zoho/sync-activities`, {
        method: 'POST'
      });
      const data = await res.json();
      
      if (data.results) {
        toast.success(`Synced ${data.results.synced} students to Zoho`);
        loadSyncQueue();
        loadDashboard();
      }
    } catch (error) {
      console.error('Error syncing:', error);
      toast.error('Failed to sync activities to Zoho');
    } finally {
      setSyncing(false);
    }
  };

  const filteredStudents = students.filter(s => 
    s.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.student_email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout>
      <div className="p-6 space-y-6" data-testid="student-activity-manager">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Student Activity Manager</h1>
            <p className="text-slate-500">Track all student portal activities, AI Finder searches, and tool usage</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { loadDashboard(); loadStudents(); loadSyncQueue(); }}>
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button 
              onClick={triggerZohoSync} 
              disabled={syncing || syncQueue.length === 0}
              className="bg-ux-ocean hover:bg-ux-ocean/90"
            >
              {syncing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
              Sync to Zoho ({syncQueue.length})
            </Button>
          </div>
        </div>

        {/* Dashboard Stats */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Total Activities</p>
                    <p className="text-2xl font-bold">{dashboard.total_activities?.toLocaleString()}</p>
                  </div>
                  <Activity className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Active Students</p>
                    <p className="text-2xl font-bold">{dashboard.total_active_students}</p>
                  </div>
                  <Users className="w-8 h-8 text-emerald-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Pending Zoho Sync</p>
                    <p className="text-2xl font-bold">{dashboard.zoho_sync_status?.pending || 0}</p>
                  </div>
                  <Clock className="w-8 h-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500">Synced to Zoho</p>
                    <p className="text-2xl font-bold">{dashboard.zoho_sync_status?.synced || 0}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="students">Students ({students.length})</TabsTrigger>
            <TabsTrigger value="ai-finder">AI Finder ({aiFinderActivities.length})</TabsTrigger>
            <TabsTrigger value="sync-queue">Sync Queue ({syncQueue.length})</TabsTrigger>
            <TabsTrigger value="zoho-fields">Zoho Fields</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            {dashboard && (
              <>
                {/* Category Breakdown */}
                <Card>
                  <CardHeader>
                    <CardTitle>Activity by Category</CardTitle>
                    <CardDescription>Distribution of activities across different categories</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      {Object.entries(dashboard.category_counts || {}).map(([category, count]) => (
                        <div key={category} className="p-4 rounded-lg bg-slate-50">
                          <Badge className={CATEGORY_COLORS[category] || 'bg-gray-100 text-gray-800'}>
                            {category.replace('_', ' ')}
                          </Badge>
                          <p className="text-2xl font-bold mt-2">{count}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Top Activity Types */}
                <Card>
                  <CardHeader>
                    <CardTitle>Top Activity Types</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {dashboard.top_activity_types?.map((type, idx) => (
                        <div key={type.type} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <span className="text-lg font-medium text-slate-400">#{idx + 1}</span>
                            <div>
                              <p className="font-medium">{type.label}</p>
                              <p className="text-sm text-slate-500">{type.type}</p>
                            </div>
                          </div>
                          <Badge variant="secondary">{type.count}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Students Tab */}
          <TabsContent value="students">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>Students with Activities</CardTitle>
                  <div className="flex items-center gap-2">
                    <Search className="w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search students..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-64"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Activities</TableHead>
                      <TableHead>Categories</TableHead>
                      <TableHead>Last Active</TableHead>
                      <TableHead>Zoho Link</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredStudents.map((student) => (
                      <TableRow key={student.user_id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{student.student_name}</p>
                            <p className="text-sm text-slate-500">{student.student_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="secondary">{student.total_activities}</Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {student.categories?.slice(0, 3).map(cat => (
                              <Badge key={cat} className={`text-xs ${CATEGORY_COLORS[cat] || ''}`}>
                                {cat?.replace('_', ' ')}
                              </Badge>
                            ))}
                            {student.categories?.length > 3 && (
                              <Badge variant="outline" className="text-xs">+{student.categories.length - 3}</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDate(student.last_activity)}
                        </TableCell>
                        <TableCell>
                          {student.zoho_lead_id ? (
                            <a 
                              href={`https://crm.zoho.in/crm/org123/tab/Leads/${student.zoho_lead_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline flex items-center gap-1"
                            >
                              <ExternalLink className="w-3 h-3" />
                              View in Zoho
                            </a>
                          ) : (
                            <span className="text-slate-400 text-sm">Not linked</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => {
                              setSelectedStudent(student);
                              loadStudentActivities(student.user_id);
                            }}
                          >
                            <Eye className="w-4 h-4 mr-1" /> View
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* AI Finder Tab */}
          <TabsContent value="ai-finder">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-purple-500" />
                  AI University Finder Activities
                </CardTitle>
                <CardDescription>
                  Comprehensive view of AI Finder searches, results, and selections (CMS only - not synced to Zoho)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Activity Type</TableHead>
                      <TableHead>Details</TableHead>
                      <TableHead>Timestamp</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {aiFinderActivities.map((activity) => (
                      <TableRow key={activity.activity_id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{activity.student_name || 'Unknown'}</p>
                            <p className="text-sm text-slate-500">{activity.student_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className="bg-purple-100 text-purple-800">
                            {activity.activity_type?.replace('ai_finder_', '')}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="max-w-md">
                            <p className="font-medium">{activity.title}</p>
                            {activity.description && (
                              <p className="text-sm text-slate-500 truncate">{activity.description}</p>
                            )}
                            {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                              <details className="mt-1">
                                <summary className="text-xs text-blue-600 cursor-pointer">View metadata</summary>
                                <pre className="text-xs bg-slate-50 p-2 rounded mt-1 overflow-auto max-h-32">
                                  {JSON.stringify(activity.metadata, null, 2)}
                                </pre>
                              </details>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDate(activity.created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {aiFinderActivities.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-slate-500 py-8">
                          No AI Finder activities recorded yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sync Queue Tab */}
          <TabsContent value="sync-queue">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Pending Zoho Sync</CardTitle>
                    <CardDescription>Activities that will be synced to Zoho CRM as Notes</CardDescription>
                  </div>
                  <Button 
                    onClick={triggerZohoSync} 
                    disabled={syncing || syncQueue.length === 0}
                  >
                    {syncing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    Sync All Now
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Student</TableHead>
                      <TableHead>Activity</TableHead>
                      <TableHead>Zoho Lead ID</TableHead>
                      <TableHead>Timestamp</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {syncQueue.map((item) => (
                      <TableRow key={item.activity_id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{item.student_name || 'Unknown'}</p>
                            <p className="text-sm text-slate-500">{item.student_email}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{item.title}</p>
                            <Badge variant="outline" className="text-xs">{item.label}</Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          {item.zoho_lead_id ? (
                            <code className="text-xs bg-slate-100 px-2 py-1 rounded">{item.zoho_lead_id}</code>
                          ) : (
                            <span className="text-red-500 text-sm">Missing - cannot sync</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDate(item.created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                    {syncQueue.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-slate-500 py-8">
                          <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                          All activities are synced to Zoho
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Zoho Fields Tab */}
          <TabsContent value="zoho-fields">
            <Card>
              <CardHeader>
                <CardTitle>Zoho CRM Custom Module Structure</CardTitle>
                <CardDescription>
                  Recommended fields to create in Zoho CRM for comprehensive student activity tracking
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Current Implementation */}
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <h3 className="font-semibold text-green-800 mb-2">Current Implementation: Notes Module</h3>
                  <p className="text-green-700 text-sm mb-3">
                    Activities are synced as Notes attached to Leads. Each batch sync creates a summary note.
                  </p>
                  <div className="bg-white p-3 rounded border">
                    <p className="font-medium">Note Structure:</p>
                    <ul className="text-sm text-slate-600 mt-2 space-y-1">
                      <li>• <strong>Note_Title:</strong> "Portal Activity Update - [Date]"</li>
                      <li>• <strong>Note_Content:</strong> Formatted activity summary by category</li>
                      <li>• <strong>Parent_Id:</strong> Linked to Lead ID</li>
                    </ul>
                  </div>
                </div>

                {/* Custom Module Recommendation */}
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <h3 className="font-semibold text-blue-800 mb-2">Recommended: Custom "Student Activities" Module</h3>
                  <p className="text-blue-700 text-sm mb-3">
                    For granular tracking, create a custom module in Zoho CRM with these fields:
                  </p>
                  
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-blue-100">
                        <TableHead>Field Name</TableHead>
                        <TableHead>Field Type</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Sync</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium">Activity_ID</TableCell>
                        <TableCell>Single Line</TableCell>
                        <TableCell>Unique identifier from portal</TableCell>
                        <TableCell><Badge>Yes</Badge></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Lead_ID</TableCell>
                        <TableCell>Lookup (Leads)</TableCell>
                        <TableCell>Link to parent Lead record</TableCell>
                        <TableCell><Badge>Yes</Badge></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Activity_Type</TableCell>
                        <TableCell>Picklist</TableCell>
                        <TableCell>application_created, document_uploaded, etc.</TableCell>
                        <TableCell><Badge>Yes</Badge></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Activity_Category</TableCell>
                        <TableCell>Picklist</TableCell>
                        <TableCell>application, document, milestone, communication</TableCell>
                        <TableCell><Badge>Yes</Badge></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Title</TableCell>
                        <TableCell>Single Line</TableCell>
                        <TableCell>Activity title/summary</TableCell>
                        <TableCell><Badge>Yes</Badge></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Description</TableCell>
                        <TableCell>Multi Line</TableCell>
                        <TableCell>Detailed activity description</TableCell>
                        <TableCell><Badge>Yes</Badge></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Activity_Date</TableCell>
                        <TableCell>Date/Time</TableCell>
                        <TableCell>When the activity occurred</TableCell>
                        <TableCell><Badge>Yes</Badge></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">University_Name</TableCell>
                        <TableCell>Single Line</TableCell>
                        <TableCell>Related university (if applicable)</TableCell>
                        <TableCell><Badge>Yes</Badge></TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Document_Type</TableCell>
                        <TableCell>Picklist</TableCell>
                        <TableCell>For document activities: passport, transcript, etc.</TableCell>
                        <TableCell><Badge>Yes</Badge></TableCell>
                      </TableRow>
                      <TableRow className="bg-amber-50">
                        <TableCell className="font-medium">AI_Search_Criteria</TableCell>
                        <TableCell>Multi Line</TableCell>
                        <TableCell>AI Finder search parameters (JSON)</TableCell>
                        <TableCell><Badge variant="outline">CMS Only</Badge></TableCell>
                      </TableRow>
                      <TableRow className="bg-amber-50">
                        <TableCell className="font-medium">AI_Results_Count</TableCell>
                        <TableCell>Number</TableCell>
                        <TableCell>Number of universities returned</TableCell>
                        <TableCell><Badge variant="outline">CMS Only</Badge></TableCell>
                      </TableRow>
                      <TableRow className="bg-amber-50">
                        <TableCell className="font-medium">Calculator_Input</TableCell>
                        <TableCell>Multi Line</TableCell>
                        <TableCell>Loan/GPA calculator inputs (JSON)</TableCell>
                        <TableCell><Badge variant="outline">CMS Only</Badge></TableCell>
                      </TableRow>
                      <TableRow className="bg-amber-50">
                        <TableCell className="font-medium">Calculator_Result</TableCell>
                        <TableCell>Multi Line</TableCell>
                        <TableCell>Calculator output/result</TableCell>
                        <TableCell><Badge variant="outline">CMS Only</Badge></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>

                  <div className="mt-4 p-3 bg-amber-50 rounded border border-amber-200">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> Fields marked "CMS Only" are stored in the portal database but not synced to Zoho 
                      to keep CRM clean. Counselors can view detailed AI Finder and calculator data in this CMS page.
                    </p>
                  </div>
                </div>

                {/* API Endpoint Info */}
                <div className="p-4 bg-slate-50 rounded-lg border">
                  <h3 className="font-semibold mb-2">API Endpoints for Integration</h3>
                  <div className="space-y-2 text-sm font-mono">
                    <p><strong>POST</strong> /api/zoho/sync-activities - Trigger batch sync</p>
                    <p><strong>GET</strong> /api/zoho/sync-activities/preview - Preview pending sync</p>
                    <p><strong>GET</strong> /api/zoho/sync-activities/history - View sync history</p>
                    <p><strong>GET</strong> /api/activity/admin/dashboard - Activity dashboard stats</p>
                    <p><strong>GET</strong> /api/activity/admin/student/{'{user_id}'} - Full student activity</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Student Detail Dialog */}
        <Dialog open={!!selectedStudent} onOpenChange={() => { setSelectedStudent(null); setStudentActivities(null); }}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedStudent?.student_name}'s Activity
              </DialogTitle>
              <DialogDescription>
                {selectedStudent?.student_email} • {selectedStudent?.total_activities} total activities
              </DialogDescription>
            </DialogHeader>
            
            {studentActivities && (
              <div className="space-y-4">
                {/* Category Summary */}
                <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                  {Object.entries(studentActivities.category_summary || {}).map(([cat, data]) => (
                    <div key={cat} className="p-2 rounded bg-slate-50 text-center">
                      <Badge className={`text-xs ${CATEGORY_COLORS[cat] || ''}`}>{cat}</Badge>
                      <p className="font-bold mt-1">{data.count}</p>
                    </div>
                  ))}
                </div>

                {/* AI Finder Searches */}
                {studentActivities.ai_finder_searches?.length > 0 && (
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold flex items-center gap-2 mb-3">
                      <Sparkles className="w-4 h-4 text-purple-500" />
                      AI Finder Searches ({studentActivities.ai_finder_searches.length})
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {studentActivities.ai_finder_searches.map((search, idx) => (
                        <div key={idx} className="p-2 bg-purple-50 rounded text-sm">
                          <p className="font-medium">{search.title}</p>
                          <p className="text-slate-600">{search.description}</p>
                          <p className="text-xs text-slate-400 mt-1">{formatDate(search.created_at)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Calculator Usage */}
                {studentActivities.calculator_usage?.length > 0 && (
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold flex items-center gap-2 mb-3">
                      <Calculator className="w-4 h-4 text-amber-500" />
                      Calculator Usage ({studentActivities.calculator_usage.length})
                    </h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {studentActivities.calculator_usage.map((calc, idx) => (
                        <div key={idx} className="p-2 bg-amber-50 rounded text-sm">
                          <p className="font-medium">{calc.title}</p>
                          {calc.metadata && (
                            <pre className="text-xs bg-white p-1 rounded mt-1 overflow-auto">
                              {JSON.stringify(calc.metadata, null, 2)}
                            </pre>
                          )}
                          <p className="text-xs text-slate-400 mt-1">{formatDate(calc.created_at)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Activities */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold mb-3">Recent Activities</h4>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {studentActivities.activities?.slice(0, 20).map((activity, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-2 hover:bg-slate-50 rounded">
                        <Badge className={`text-xs ${CATEGORY_COLORS[activity.category] || ''}`}>
                          {activity.category}
                        </Badge>
                        <div className="flex-1">
                          <p className="font-medium text-sm">{activity.title}</p>
                          {activity.description && (
                            <p className="text-xs text-slate-500">{activity.description}</p>
                          )}
                        </div>
                        <span className="text-xs text-slate-400">{formatDate(activity.created_at)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
